// One request per resource, bounded event debounce, and an explicit route/session epoch.
export function createBackgroundRefresh({ scope, hidden = () => false, delay = 250, maxDelay = 1000, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout }) {
  const entries = new Map();
  let epoch = 0;
  function arm(entry) {
    if (entry.running || hidden()) return;
    clearTimer(entry.timer);
    entry.timer = setTimer(() => void run(entry), Math.max(0, Math.min(delay, entry.first + maxDelay - now())));
  }
  async function run(entry) {
    entry.timer = null;
    if (entries.get(entry.key) !== entry || hidden()) return;
    entry.pending = false; entry.running = true;
    const controller = new AbortController(), startedEpoch = epoch, startedScope = scope();
    entry.controller = controller;
    const isCurrent = () => !controller.signal.aborted && epoch === startedEpoch && scope() === startedScope && !hidden();
    try { await entry.work({ signal: controller.signal, isCurrent }); }
    catch { /* Keep the visible snapshot; subsequent events retry. */ }
    finally {
      entry.running = false;
      if (entries.get(entry.key) === entry && entry.pending) { entry.first = now(); arm(entry); }
    }
  }
  return {
    schedule(key, work) {
      let entry = entries.get(key);
      if (!entry) { entry = { key, running: false, pending: false }; entries.set(key, entry); }
      entry.work = work;
      if (!entry.pending) entry.first = now();
      entry.pending = true;
      arm(entry);
    },
    reset() {
      epoch++;
      for (const entry of entries.values()) { clearTimer(entry.timer); entry.controller?.abort(); }
      entries.clear();
    },
    visibilityChanged() {
      for (const entry of entries.values()) {
        if (hidden()) {
          clearTimer(entry.timer); entry.timer = null;
          if (entry.running) { entry.pending = true; entry.controller?.abort(); }
        } else if (entry.pending) { entry.first = now(); arm(entry); }
      }
    }
  };
}
